import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

export function About() {
  useEffect(() => {
    document.title = "vibe-check ⏵About";
  }, []);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyToClipboard = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="py-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-white mb-4">About Vibe-Check</h1>
        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
          Turn your Claude Code conversations into queryable, shareable data.
          Archive, analyze, and share your AI-assisted development sessions.
        </p>
      </div>

      {/* Installation Instructions */}
      <div className="max-w-2xl mx-auto mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6 text-center">
          Install the Client
        </h2>

        {/* macOS Homebrew */}
        <div className="bg-gray-800 rounded-lg p-5 mb-4 text-left">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-medium text-purple-400">
              macOS (Homebrew)
            </h3>
            <button
              onClick={() =>
                copyToClipboard(
                  "brew install wanderingstan/vibe-check/vibe-check && vibe-check start",
                  "brew",
                )
              }
              className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-300 transition flex items-center gap-1"
            >
              {copiedId === "brew" ? (
                <>
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>
          <pre className="bg-gray-900 rounded p-3 text-sm text-gray-300 overflow-x-auto">
            <code>
              brew install wanderingstan/vibe-check/vibe-check && vibe-check
              start
            </code>
          </pre>
        </div>

        {/* Linux / Other */}
        <div className="bg-gray-800 rounded-lg p-5 text-left">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-medium text-purple-400">
              Linux / Other
            </h3>
            <button
              onClick={() =>
                copyToClipboard(
                  "curl -fsSL https://vibecheck.wanderingstan.com/install.sh | bash",
                  "curl",
                )
              }
              className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-300 transition flex items-center gap-1"
            >
              {copiedId === "curl" ? (
                <>
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>
          <pre className="bg-gray-900 rounded p-3 text-sm text-gray-300 overflow-x-auto">
            <code>
              curl -fsSL https://vibecheck.wanderingstan.com/install.sh | bash
            </code>
          </pre>
        </div>

        <p className="mt-4 text-sm text-gray-500 text-center">
          After installing, the client will guide you through authentication.
        </p>
      </div>

      {/* Features */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-white mb-8 text-center">
          Why Vibe-Check?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Feature 1: Local SQLite Archive */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-purple-400 mb-3">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Local SQLite Archive
            </h3>
            <p className="text-gray-400 mb-3">
              Every message stored locally in SQLite. Query your conversation
              history with SQL. Full database access for power users.
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• JSON flexibility + generated columns</li>
              <li>
                • Direct <code className="text-purple-400">vibe_sql</code> MCP
                tool
              </li>
              <li>• Run arbitrary SQL queries</li>
            </ul>
          </div>

          {/* Feature 2: MCP Introspection */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-purple-400 mb-3">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">
              MCP Introspection
            </h3>
            <p className="text-gray-400 mb-3">
              Eight MCP tools let Claude analyze its own conversation history.
              Ask "What did we talk about last week?" or "Show me sessions where
              we discussed agents."
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• Full-text search with FTS5</li>
              <li>• Usage statistics & tool analysis</li>
              <li>• Session summaries & deep dives</li>
            </ul>
          </div>

          {/* Feature 3: Granular Sharing */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-purple-400 mb-3">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Granular Sharing
            </h3>
            <p className="text-gray-400 mb-3">
              Share specific conversations, sessions, or repositories. Custom
              URLs, expiration dates, and access control. Multiple export
              formats.
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• Message, session, or repo-level sharing</li>
              <li>• Custom slugs & expiration dates</li>
              <li>• HTML, markdown, plaintext formats</li>
            </ul>
          </div>

          {/* Feature 4: Git Provenance */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-purple-400 mb-3">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Git Provenance
            </h3>
            <p className="text-gray-400 mb-3">
              Track which conversations led to which code changes. Link sessions
              to commits. Filter conversations by repository.
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• Remote URL, commit hash tracking</li>
              <li>• File path & line number context</li>
              <li>• Link conversations to code changes</li>
            </ul>
          </div>

          {/* Feature 5: Community Leaderboard */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-purple-400 mb-3">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Community Leaderboard
            </h3>
            <p className="text-gray-400 mb-3">
              See how your usage compares to other developers. Public aggregate
              stats and rankings updated daily.
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• Top 5 users ranked by activity</li>
              <li>• Daily trend charts</li>
              <li>• Anonymous but competitive</li>
            </ul>
          </div>

          {/* Feature 6: Privacy-First */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-purple-400 mb-3">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Privacy-First
            </h3>
            <p className="text-gray-400 mb-3">
              Automatic detection and redaction of API keys, tokens, and
              credentials before data leaves your machine. Built on the{" "}
              <code className="text-purple-400">detect-secrets</code> library.
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• 20+ secret types detected</li>
              <li>• AWS, GitHub, JWT, Stripe tokens</li>
              <li>• Redacted before network transmission</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Use Cases */}
      <div className="mb-16 max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold text-white mb-8 text-center">
          Use Cases
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-2">
              Debug Complex Sessions
            </h3>
            <p className="text-gray-400 text-sm">
              Search through past conversations to find that solution Claude
              gave you three days ago. Full-text search across all your
              sessions.
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-2">
              Share Solutions with Teammates
            </h3>
            <p className="text-gray-400 text-sm">
              Export and share specific debugging sessions or architectural
              decisions. Custom URLs make it easy to reference in PRs or docs.
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-2">
              Learn from Your Patterns
            </h3>
            <p className="text-gray-400 text-sm">
              Analyze which tools you use most, what times you're most
              productive, and how your workflow evolves over time.
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-2">
              Track Token Spending
            </h3>
            <p className="text-gray-400 text-sm">
              Monitor your Claude usage across repositories and projects. See
              which sessions consume the most tokens.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="mb-16 max-w-3xl mx-auto">
        <h2 className="text-3xl font-bold text-white mb-8 text-center">FAQ</h2>
        <div className="space-y-6">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-2">
              Is it free?
            </h3>
            <p className="text-gray-400 text-sm">
              Yes, vibe-check is currently free to use. The client is open
              source, and the hosted web service is provided at no cost.
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-2">
              What data is collected?
            </h3>
            <p className="text-gray-400 text-sm">
              All Claude Code conversation events are stored locally in SQLite
              first. With your permission, the client can sync to our servers
              for web access and sharing. API keys, tokens, and credentials are
              automatically redacted before any network transmission.
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-2">
              Can I self-host?
            </h3>
            <p className="text-gray-400 text-sm">
              The client works entirely offline with its local SQLite database.
              You can use all MCP introspection tools without ever syncing to
              our servers. Self-hosting the web interface is possible but not
              officially supported yet.
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-2">
              How do I delete my data?
            </h3>
            <p className="text-gray-400 text-sm">
              You can delete all your synced events from the Settings page.
              Local data can be deleted by removing the SQLite database file.
            </p>
          </div>
        </div>
      </div>

      {/* Link to Community */}
      <div className="text-center mt-12">
        <Link
          to="/community"
          className="text-purple-400 hover:text-purple-300 underline"
        >
          View community statistics →
        </Link>
      </div>
    </div>
  );
}
